import { Component, Input, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GmrDataService } from '../core/gmr-data.service';
import { GmrFunctionParameter, GmrLovRow } from '../core/gmr.models';
import { LovSelectComponent } from './lov-select.component';

//
// 1:1 port of one <li ng-repeat="myFunctionParameter in myFunctionParameters"> in menu.html: a
// plain input (text/checkbox/date/...), OR a LOV select, plus the user-hint tooltip.
//
@Component({
  selector: 'app-parameter-field',
  standalone: true,
  imports: [FormsModule, TranslateDirective, TranslatePipe, LovSelectComponent],
  //
  // Root template is a single <li>, rendered inside a plain <ul> in parameter-form's template -
  // display:contents makes it a real direct child of that <ul> for layout/list purposes instead
  // of sitting one inline-boxed level deeper inside the custom element.
  //
  styles: [':host { display: contents; }'],
  templateUrl: './parameter-field.component.html',
})
export class ParameterFieldComponent
{
  @Input({ required: true }) parameter!: GmrFunctionParameter;
  @Input() lovRows: GmrLovRow[] = [];

  //
  // The report this field belongs to, needed only to qualify the DOM id below. Passed in rather than
  // read off `parameter.cfn_code`: that column is the function's business key within its application
  // (`gmr_cfn_nk`), whereas the preset name is what the router, the form action and the report
  // definition file all key on, so it is the identity the rest of the app already agrees on.
  //
  @Input({ required: true }) presetName!: string;

  private readonly data = inject(GmrDataService);

  //
  // Unique, stable DOM id for this field, and the target of the <label for>.
  //
  // `gmr_cfr_nk` (`ddl/tables.sql`) makes a parameter code unique within one report, so
  // report + parameter code is the narrowest key that cannot collide - which matters because a
  // parameter code as ordinary as `division_codes` recurs in almost every report. See
  // ai/DATAMODEL.md for the full key chain and for why the bare `cfr_code` was not enough.
  //
  // Only the `id` is namespaced. The `name` attribute stays the bare `cfr_code`: it is what the
  // backend receives on submit and what HashStateService.writeBookmark() serialises into the
  // bookmark hash, so qualifying it would break both downloads and every existing bookmark.
  //
  get fieldId(): string
  {
    return `param-${this.presetName}-${this.parameter.cfr_code}`;
  }

  get hasLov(): boolean
  {
    return this.parameter.cfr_lov_preset != null;
  }

  get isRequired(): boolean
  {
    return this.parameter.cfr_required_flag === true && this.parameter.data_ind_html_control !== 'checkbox';
  }

  get isChecked(): boolean
  {
    return this.parameter.cfr_default_value === 'true' && this.parameter.data_ind_html_control === 'checkbox';
  }

  //
  // 1:1 port of the placeholder ternary - looks like broken operator precedence at a glance
  // (`a ? b : c || d ? e : f`) but `'' || (cond)` evaluates to `cond`, so it already does exactly
  // what it looks like it should: 'Type at least 3 characters' for search_string, 'yyyy-mm-dd'
  // for date fields, empty otherwise. Written out explicitly here instead of reproducing the
  // confusing-but-correct original expression.
  //
  get placeholder(): string
  {
    if (this.parameter.cfr_code === 'search_string') return 'Type at least 3 characters';
    if (this.parameter.data_ind_html_control === 'date') return 'yyyy-mm-dd';
    return '';
  }

  onTextChange(): void
  {
    this.data.lovTextChanged(this.parameter);
  }

  onCheckboxChange(checked: boolean): void
  {
    this.parameter.cfr_default_value = checked ? 'true' : 'false';
  }
}
